import { ReactElement, useMemo, useCallback } from 'react';
import { useFormik, FormikHelpers, FormikProvider } from 'formik';
import {
  Stack,
  Typography,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { isEmpty } from 'lodash';

import {
  ColorPicker,
  SelectZoneLogisticType,
  ListField,
} from '~components/index';
import { hasError } from '~utils/formHelpers';
import {
  isLogisticTypeRange,
  isLogisticTypeGeometryArea,
} from '~utils/logisiticsZones';

import {
  ConfigLogisticZoneFormProps,
  ConfigLogisticZoneFormValues,
} from './types';
import { FIELDS_NAME, getInitialValues, validationSchema } from './utils';
import {
  ConfigLogisticZoneFormContent,
  ConfigLogisticZoneFormInputsContent,
} from './styles';
import ConfigLogisticZoneFormMap from './Map';

const ConfigLogisticZoneForm = ({
  onSubmit,
  loadingSubmit = false,
  data,
  type = 'create',
  warehouses,
}: ConfigLogisticZoneFormProps): ReactElement => {
  const isCreate = useMemo(() => type === 'create', [type]);

  const executeSubmit = useCallback(
    async (
      values: ConfigLogisticZoneFormValues,
      { setSubmitting }: FormikHelpers<ConfigLogisticZoneFormValues>,
    ) => {
      await onSubmit(values);

      setSubmitting(false);
    },
    [onSubmit],
  );

  const formikBag = useFormik({
    initialValues: getInitialValues(data),
    validationSchema,
    onSubmit: executeSubmit,
  });

  const {
    errors,
    touched,
    getFieldProps,
    handleSubmit,
    values,
    isSubmitting,
    setFieldValue,
    setFieldTouched,
    dirty,
  } = formikBag;

  const handleChangeColor = useCallback(
    (newColor: string) => {
      setFieldValue(FIELDS_NAME.COLOR, newColor);
    },
    [setFieldValue],
  );

  const handleBlurColor = useCallback(() => {
    setFieldTouched(FIELDS_NAME.COLOR, true);
  }, [setFieldTouched]);

  const currentZoneTypeId = useMemo(
    () => values[FIELDS_NAME.ZONE_TYPE_ID],
    [values],
  );

  const isSelectedZoneTypeRange = useMemo(
    () =>
      !isEmpty(currentZoneTypeId) &&
      isLogisticTypeRange(Number(currentZoneTypeId)),
    [currentZoneTypeId],
  );

  const isSelectedZoneTypeGeometryArea = useMemo(
    () =>
      !isEmpty(currentZoneTypeId) &&
      isLogisticTypeGeometryArea(Number(currentZoneTypeId)),
    [currentZoneTypeId],
  );

  return (
    <FormikProvider value={formikBag}>
      <Stack spacing={2} component="form" onSubmit={handleSubmit}>
        <ConfigLogisticZoneFormContent direction="row" spacing={2}>
          <ConfigLogisticZoneFormInputsContent spacing={2} flex={1}>
            <Stack spacing={2}>
              <Typography component="h6" fontWeight="bold">
                Información de la zona
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    {...getFieldProps(FIELDS_NAME.ENABLED)}
                    checked={values[FIELDS_NAME.ENABLED]}
                  />
                }
                label="Activa"
                sx={{ maxWidth: 'max-content' }}
              />

              <Stack direction="row" spacing={2}>
                <TextField
                  label="Código de zona"
                  autoComplete="off"
                  fullWidth
                  error={hasError(touched, errors, FIELDS_NAME.CODE)}
                  {...getFieldProps(FIELDS_NAME.CODE)}
                />

                <ColorPicker
                  value={values[FIELDS_NAME.COLOR]}
                  label="Color"
                  fullWidth
                  onChange={handleChangeColor}
                  onBlur={handleBlurColor}
                  error={hasError(touched, errors, FIELDS_NAME.COLOR)}
                />
              </Stack>

              <TextField
                label="Descripción"
                autoComplete="off"
                fullWidth
                error={hasError(touched, errors, FIELDS_NAME.DESCRIPTION)}
                {...getFieldProps(FIELDS_NAME.DESCRIPTION)}
              />

              <SelectZoneLogisticType
                label="Tipo de zona"
                autoComplete="off"
                fullWidth
                error={hasError(touched, errors, FIELDS_NAME.ZONE_TYPE_ID)}
                {...getFieldProps(FIELDS_NAME.ZONE_TYPE_ID)}
              />

              {isSelectedZoneTypeRange && (
                <ListField
                  name={FIELDS_NAME.RANGE_DESCRIPTION}
                  label="Delimitación de zona"
                  // eslint-disable-next-line max-len
                  description="Puedes especificar uno o varios códigos postales, o definir un intervalo. Ej: 1200,1205,1500-1800."
                  addLabel="Nuevo rango"
                  addTooltip="Agregar rango"
                  deleteTooltip="Eliminar rango"
                  addItemPosition="last"
                />
              )}
            </Stack>
          </ConfigLogisticZoneFormInputsContent>

          <Stack spacing={2} flex={0.7}>
            {isSelectedZoneTypeGeometryArea && (
              <ConfigLogisticZoneFormMap warehouses={warehouses} />
            )}
          </Stack>
        </ConfigLogisticZoneFormContent>

        <Stack direction="row" spacing={2}>
          <LoadingButton
            type="submit"
            variant="contained"
            color="primary"
            loading={loadingSubmit || isSubmitting}
            disabled={!isCreate && !dirty}
            sx={{ minWidth: 200 }}
          >
            {isCreate ? 'Crear' : 'Guardar'}
          </LoadingButton>
        </Stack>
      </Stack>
    </FormikProvider>
  );
};

export default ConfigLogisticZoneForm;
